import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { Link } from "@/i18n/Link";
import { Phone, MapPin, Zap, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE, SITE_PHONE_DISPLAY, SITE_URL } from "@/routes-data";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    seoTitle: "Emergency & Same-Day Trailer Repair in Elkhart, IN | Axle Inc.",
    seoDesc: "Trailer down or due for service? Axle Inc. does most bearing repacks, brake jobs, and axle swaps same day in Elkhart — no appointment needed. Call (574) 264-9434 for breakdown advice.",
    breadHome: "Home",
    breadServices: "Services",
    breadCurrent: "Emergency & Same-Day Trailer Repair",
    zapLabel: "Emergency & Same-Day Service",
    h1: "Emergency & Same-Day Trailer Repair in Elkhart and Northern Indiana",
    lastUpdated: "Last updated: July 2026",
    tldr: (phone: string) => `TL;DR: Most bearing repacks, brake jobs, hub replacements, and standard axle swaps are done the same day you drop off — no appointment needed during business hours. Trailer down on the road? Call ${phone} first. We'll tell you if it's safe to drive or tow in, Mon–Fri 8am–5pm.`,
    callNow: "Call now",
    scheduleOnline: "Schedule online",
    whyMattersH2: "Why same-day matters for a trailer breakdown",
    whyMattersP1: "A trailer sitting idle costs money — a loaded trailer that can't roll costs more. At Axle Inc. we stock Dexter components for every axle from 1,000 to 10,000 lb, which means we're almost never waiting on a parts delivery. Most jobs that come in before noon leave the same afternoon.",
    whyMattersP2: "For roadside situations: call before you tow. Towing a seized bearing or a locked hub on a conventional axle can destroy the spindle and hub — turning a $150 bearing job into a $500–$800 axle rebuild. A two-minute phone call tells you whether to drive it in or call a flatbed.",
    whatWeCanDoH2: "What we can do the same day",
    tableService: "Service",
    tableTurnaround: "Typical turnaround",
    sameDayServices: [
      { service: "Wheel bearing repack", turnaround: "Same day — most done in 1–2 hrs" },
      { service: "Bearing replacement (bearing + race + seal)", turnaround: "Same day" },
      { service: "Electric brake replacement & adjustment", turnaround: "Same day per axle" },
      { service: "Hub and drum replacement", turnaround: "Same day" },
      { service: "Seal replacement", turnaround: "Same day" },
      { service: "Breakaway kit & brake wiring checks", turnaround: "Same day" },
      { service: "Suspension inspection (springs, shackles, equalizers)", turnaround: "Same day" },
      { service: "Standard bolt-on axle replacement (in-stock axle)", turnaround: "Same day in most cases" },
      { service: "Custom axle spec, measurement & order", turnaround: "Measurement same day; build time quoted at order" },
    ],
    weldingNote: "Jobs that need welding, frame straightening, or a press — bent frames, cracked crossmembers — take longer. We'll tell you what category yours is in before we start.",
    guideH2: "Drive it in or call first? 60-second guide",
    tableSituation: "Situation",
    tableAction: "What to do",
    decisionRows: [
      { situation: "Seized bearing, locked brake, or failed hub — trailer won't roll safely", action: "Call us first. If it can't safely make it to the shop, call a rollback tow." },
      { situation: "Trailer rolls but you hear grinding, feel heat at the hub, or smell grease", action: "Drive it in — today. A bearing going bad is a $150 fix; waiting makes it a $500+ fix." },
      { situation: "Brake drag, pull, or weak stopping", action: "Drive in or schedule online. Same-day brake jobs are routine here." },
      { situation: "Visible bend in the axle tube or scored spindle", action: "Tow it in. Don't load or tow a bent axle — we'll have a straight answer and a price ready when you arrive." },
      { situation: "Seasonal bearing service for a fleet of trailers", action: "Call to schedule. We batch fleet units efficiently at the shop." },
      { situation: "Routine annual bearing repack", action: "Drop in or schedule online. No appointment needed during business hours." },
    ],
    serviceAreaH2: "Customers we serve across northern Indiana and southwest Michigan",
    serviceAreaDesc: "Based at 53664 County Road 9 in Elkhart. Customers regularly make the drive from these areas for same-day service:",
    indianaLabel: "Indiana",
    michiganLabel: "Michigan",
    nearbyNote: "Not in the list but nearby? Call anyway — same-day service is about parts availability and shop capacity, not geography.",
    howItWorksH2: "How same-day service works",
    steps: [
      { title: (ph: string) => `Call ${ph} or schedule online.`, desc: "Describe what the trailer is doing and tell us the axle capacity if you know it. We'll confirm we have the parts and give you an arrival window." },
      { title: "Drop the trailer at 53664 County Road 9, Elkhart.", desc: "No appointment needed during business hours (Mon–Fri 8am–5pm). Scheduling online holds a bay." },
      { title: "We diagnose and give you a firm price.", desc: "No surprise invoices — you approve the scope before we start. Most decisions take 15 minutes." },
      { title: "Pick up the same day.", desc: "Drop-offs before noon are almost always done the same afternoon. We call when it's ready." },
    ],
    fleetsH2: "Fleets: batch service keeps your trailers rolling",
    fleetsP: "RV haulers, dealers, landscapers, and manufacturers across Elkhart County run on trailers. We schedule recurring bearing and brake service for fleets — your units cycle through the shop on a schedule that keeps the whole row in rotation, at fleet pricing. Ask for fleet scheduling when you call.",
    costH2: "How much does same-day repair cost?",
    costP: "Most common jobs run $80–$450 per axle depending on what needs doing. Full axle replacements start around $400 for idler axles. For the full price breakdown see our trailer axle replacement cost guide.",
    faqH2: "Frequently asked questions",
    faqs: [
      { q: "Do you do same-day trailer repair without an appointment?", a: "Yes — most bearing repacks, brake jobs, hub replacements, and standard axle swaps are done the same day you drop off, even without a scheduled appointment. We stock Dexter parts 1,000–10,000 lb, so we're rarely waiting on a supplier. Scheduling online guarantees your bay." },
      { q: "My trailer broke down on the road. What should I do?", a: "Call us at (574) 264-9434 and describe what the trailer is doing — grinding, locking, leaning, smoke — and your location. We'll tell you whether it's safe to tow it in or whether you need a flatbed first. Don't tow a seized hub or a locked brake; that's how a $150 repair becomes a $1,500 one." },
      { q: "How do I know if I need a repair or a full axle replacement?", a: "Bearings, seals, and brakes are maintenance — almost always a same-day repair. Bent axle tubes, scored spindles, and rust-through are replacement. If you're not sure, drop by — we'll put it on the rack and give you a straight answer before you spend anything." },
      { q: "What information should I have ready when I call?", a: "Four things help: (1) what the trailer is doing — noise, smoke, lean, lock-up; (2) trailer type and rough axle capacity; (3) your location or how far you are from the shop; (4) a photo of the hub or axle area if you can get one safely." },
      { q: "How far do customers drive to use Axle Inc.?", a: "We regularly serve customers from across Elkhart County and the surrounding region — South Bend, Warsaw, Kendallville, and Kalamazoo are common. The 20 cities in our service area are listed below. For a same-day job, the drive is almost always worth it." },
      { q: "Can you replace a trailer axle the same day?", a: "For in-stock axles — yes, most standard spring and torsion axles from 2,000–10,000 lb are on the shelf. Drop off in the morning, pick up the same afternoon in most cases. Custom-spec axles (odd hub faces, drop axles) are ordered through our Dexter desk; turnaround is quoted when you order." },
    ],
    bannerH2: "Trailer down? Call before you tow.",
    bannerP: "A 2-minute call can save a $1,500 mistake. Axle Inc. — Elkhart's authorized Dexter distributor since 1995.",
    relatedH2: "Related services",
    rel1: "In-shop trailer axle repair",
    rel2: "2026 axle replacement cost guide",
    rel3: "All service areas",
  },
  es: {
    seoTitle: "Reparación de remolques de emergencia y en el mismo día en Elkhart, IN | Axle Inc.",
    seoDesc: "¿Remolque averiado o necesita mantenimiento? Axle Inc. realiza la mayoría de los reempaquetados de rodamientos, trabajos de frenos y cambios de ejes en el mismo día en Elkhart, sin cita previa. Llame al (574) 264-9434 para recibir asesoramiento.",
    breadHome: "Inicio",
    breadServices: "Servicios",
    breadCurrent: "Reparación de remolques de emergencia y en el mismo día",
    zapLabel: "Servicio de emergencia y mismo día",
    h1: "Reparación de remolques de emergencia y en el mismo día en Elkhart y el norte de Indiana",
    lastUpdated: "Última actualización: julio 2026",
    tldr: (phone: string) => `Resumen: La mayoría de los reempaquetados de rodamientos, trabajos de frenos, reemplazos de maza y cambios estándar de ejes se completan el mismo día que usted deja su equipo, sin necesidad de cita durante el horario laboral. ¿Remolque averiado en la carretera? Llame primero al ${phone}. Le indicaremos si es seguro conducirlo o remolcarlo hasta el taller, de lunes a viernes de 8am a 5pm.`,
    callNow: "Llamar ahora",
    scheduleOnline: "Programar en línea",
    whyMattersH2: "Por qué es importante el servicio el mismo día para una avería",
    whyMattersP1: "Un remolque parado cuesta dinero; un remolque cargado que no puede rodar cuesta más. En Axle Inc. almacenamos componentes Dexter para cada eje de 1,000 a 10,000 lb, lo que significa que casi nunca esperamos por entregas. La mayoría de los trabajos que llegan antes del mediodía salen la misma tarde.",
    whyMattersP2: "Para situaciones en carretera: llame antes de remolcar. Remolcar con un rodamiento agarrotado o una maza bloqueada en un eje convencional puede destruir el husillo y la maza, convirtiendo un trabajo de rodamiento de $150 en una reconstrucción de eje de $500–$800. Una llamada de dos minutos le dirá si debe conducirlo o llamar a una grúa de plataforma.",
    whatWeCanDoH2: "Lo que podemos hacer el mismo día",
    tableService: "Servicio",
    tableTurnaround: "Tiempo de entrega típico",
    sameDayServices: [
      { service: "Reempaquetado de rodamientos de rueda", turnaround: "Mismo día — la mayoría en 1–2 hrs" },
      { service: "Reemplazo de rodamientos (rodamiento + pista + retén)", turnaround: "Mismo día" },
      { service: "Reemplazo y ajuste de frenos eléctricos", turnaround: "Mismo día por eje" },
      { service: "Reemplazo de maza y tambor", turnaround: "Mismo día" },
      { service: "Reemplazo de retenes", turnaround: "Mismo día" },
      { service: "Revisión de kit de emergencia (breakaway) y cableado de frenos", turnaround: "Mismo día" },
      { service: "Inspección de suspensión (ballestas, grilletes, ecualizadores)", turnaround: "Mismo día" },
      { service: "Reemplazo de eje estándar (eje en inventario)", turnaround: "Mismo día en la mayoría de los casos" },
      { service: "Especificación, medición y pedido de eje personalizado", turnaround: "Medición el mismo día; tiempo de fabricación citado al pedir" },
    ],
    weldingNote: "Los trabajos que requieren soldadura, enderezado de marco o prensa (marcos doblados, travesaños agrietados) tardan más tiempo. Le informaremos en qué categoría cae su equipo antes de comenzar.",
    guideH2: "¿Conducirlo o llamar primero? Guía de 60 segundos",
    tableSituation: "Situación",
    tableAction: "Qué hacer",
    decisionRows: [
      { situation: "Rodamiento agarrotado, freno bloqueado o maza fallida — el remolque no rueda con seguridad", action: "Llámenos primero. Si no puede llegar con seguridad al taller, llame a una grúa de plataforma." },
      { situation: "El remolque rueda pero escucha chirridos, siente calor en la maza o huele grasa", action: "Condúzcalo hoy mismo. Un rodamiento en mal estado es una reparación de $150; esperar lo convierte en una de $500+." },
      { situation: "Freno arrastrado, tiro desigual o frenado débil", action: "Condúzcalo hasta aquí o programe en línea. Los trabajos de frenos en el día son rutinarios." },
      { situation: "Doblez visible en el tubo del eje o husillo rayado", action: "Remólquelo. No cargue ni remolque un eje doblado — tendremos una respuesta clara y un precio listo cuando llegue." },
      { situation: "Servicio estacional de rodamientos para una flota de remolques", action: "Llame para programar. Procesamos unidades de flota eficientemente." },
      { situation: "Reempaquetado de rodamientos anual rutinario", action: "Pase o programe en línea. No se necesita cita en horario laboral." },
    ],
    serviceAreaH2: "Clientes a los que servimos en el norte de Indiana y suroeste de Michigan",
    serviceAreaDesc: "Ubicados en 53664 County Road 9 en Elkhart. Los clientes viajan regularmente desde estas áreas para servicios en el mismo día:",
    indianaLabel: "Indiana",
    michiganLabel: "Michigan",
    nearbyNote: "¿No está en la lista pero está cerca? Llame de todos modos; el servicio en el mismo día depende de la disponibilidad de piezas y capacidad del taller, no de la geografía.",
    howItWorksH2: "Cómo funciona el servicio en el mismo día",
    steps: [
      { title: (ph: string) => `Llame al ${ph} o programe en línea.`, desc: "Describa lo que hace el remolque y díganos la capacidad del eje si la conoce. Confirmaremos disponibilidad de piezas y le daremos una ventana de llegada." },
      { title: "Deje el remolque en 53664 County Road 9, Elkhart.", desc: "No se requiere cita en horario laboral (lunes a viernes, 8am–5pm). Programar en línea reserva un espacio." },
      { title: "Diagnosticamos y le damos un precio fijo.", desc: "Sin facturas sorpresa; usted aprueba el alcance antes de empezar. La mayoría de las decisiones toman 15 minutos." },
      { title: "Recójalo el mismo día.", desc: "Las entregas antes del mediodía casi siempre están listas esa misma tarde. Llamamos cuando esté listo." },
    ],
    fleetsH2: "Flotas: servicio por lotes para mantener sus remolques rodando",
    fleetsP: "Empresas de transporte de RV, concesionarios, jardineros y fabricantes en el condado de Elkhart dependen de sus remolques. Programamos servicios recurrentes de rodamientos y frenos para flotas; sus unidades pasan por el taller bajo un calendario que mantiene toda la fila en rotación, a precios de flota. Pregunte por la programación de flotas al llamar.",
    costH2: "¿Cuánto cuesta la reparación en el mismo día?",
    costP: "Los trabajos comunes cuestan entre $80 y $450 por eje según lo necesario. Los reemplazos completos de ejes comienzan cerca de $400 para ejes locos. Para ver el desglose completo de precios, consulte nuestra guía de costos de reemplazo de ejes de remolque.",
    faqH2: "Preguntas frecuentes",
    faqs: [
      { q: "¿Realizan reparaciones de remolques el mismo día sin cita?", a: "Sí, la mayoría de los reempaquetados de rodamientos, trabajos de frenos, reemplazos de maza y cambios de ejes estándar se hacen el mismo día que lo deja, incluso sin cita. Almacenamos piezas Dexter de 1,000 a 10,000 lb, así que rara vez esperamos a proveedores. Programar en línea garantiza su espacio." },
      { q: "¿Mi remolque se averió en la carretera. Qué debo hacer?", a: "Llámenos al (574) 264-9434 y describa el problema (chirrido, bloqueo, inclinación, humo) y su ubicación. Le diremos si es seguro remolcarlo o si necesita una plataforma primero. No remolque con una maza agarrotada o freno bloqueado; así es como una reparación de $150 se convierte en una de $1,500." },
      { q: "¿Cómo sé si necesito una reparación o un reemplazo total de eje?", a: "Los rodamientos, retenes y frenos son mantenimiento (casi siempre reparación en el mismo día). Los tubos de eje doblados, husillos rayados y corrosión severa requieren reemplazo. Si no está seguro, pase por aquí; lo pondremos en el elevador y le daremos una respuesta directa antes de que gaste nada." },
      { q: "¿Qué información debo tener lista cuando llame?", a: "Cuatro cosas ayudan: (1) qué hace el remolque (ruido, humo, inclinación, bloqueo); (2) tipo de remolque y capacidad aproximada del eje; (3) su ubicación o distancia al taller; (4) una foto del área de la maza o eje si puede obtenerla con seguridad." },
      { q: "¿Qué tan lejos conducen los clientes para usar Axle Inc.?", a: "Atendemos regularmente clientes de todo el condado de Elkhart y la región circundante (South Bend, Warsaw, Kendallville y Kalamazoo son comunes). Las 20 ciudades de nuestra área de servicio se enumeran a continuación. Para un trabajo de un día, casi siempre vale la pena el viaje." },
      { q: "¿Pueden reemplazar un eje de remolque el mismo día?", a: "Para ejes en inventario, sí; la mayoría de los ejes estándar de ballesta y torsión de 2,000–10,000 lb están en estante. Déjelo por la mañana, recójalo por la tarde en la mayoría de los casos. Los ejes de especificación personalizada (caras de maza inusuales, ejes caídos) se piden a través de nuestra oficina Dexter; el tiempo de entrega se cita al hacer el pedido." },
    ],
    bannerH2: "¿Remolque averiado? Llame antes de remolcar.",
    bannerP: "Una llamada de 2 minutos puede ahorrarle un error de $1,500. Axle Inc. — distribuidor autorizado de Dexter en Elkhart desde 1995.",
    relatedH2: "Servicios relacionados",
    rel1: "Reparación de ejes de remolque en taller",
    rel2: "Guía de costos de reemplazo de ejes 2026",
    rel3: "Todas las áreas de servicio",
  }
};

export default function EmergencySameDayTrailerRepair() {
  const t = useCopy(COPY);
  const path = "/services/emergency-same-day-trailer-repair/";
  const trail = [
    { name: t.breadHome, path: "/" },
    { name: t.breadServices, path: "/services/axle-repair/" },
    { name: t.breadCurrent, path },
  ];
  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path={path}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${SITE_URL}${path}#service`,
            name: t.breadCurrent,
            serviceType: "Trailer axle repair and same-day service",
            provider: { "@id": `${SITE_URL}/#business` },
            areaServed: [
              "Elkhart IN", "Granger IN", "Mishawaka IN", "Goshen IN", "South Bend IN",
              "Bristol IN", "Middlebury IN", "Nappanee IN", "Wakarusa IN", "New Paris IN",
              "Plymouth IN", "Warsaw IN", "Osceola IN", "Dunlap IN", "Millersburg IN",
              "Kendallville IN", "Niles MI", "Sturgis MI", "Cassopolis MI",
              "Benton Harbor MI", "Kalamazoo MI",
            ],
            description: "Same-day trailer axle repair at our Elkhart shop: bearing repack and replacement, electric brake service, hub and drum replacement, seal replacement, suspension inspection, and standard axle replacement. Dexter parts stocked 1,000–10,000 lb. No appointment needed.",
            url: `${SITE_URL}${path}`,
            availableChannel: {
              "@type": "ServiceChannel",
              servicePhone: {
                "@type": "ContactPoint",
                telephone: SITE_PHONE,
                contactType: "customer service",
                availableLanguage: "English",
              },
              serviceUrl: "https://axle.setmore.com",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: t.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />

      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">
            <Zap className="inline-block h-3.5 w-3.5 mr-1.5 -mt-0.5" />
            {t.zapLabel}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-4xl">
            {t.h1}
          </h1>
          <p className="mt-4 text-sm text-secondary-foreground/60">{t.lastUpdated}</p>
          <p className="mt-5 text-lg text-secondary-foreground/85 max-w-3xl leading-relaxed">
            {t.tldr(SITE_PHONE_DISPLAY)}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {t.callNow}
              </a>
            </Button>
            <ScheduleServiceDialog>
              <Button size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
                {t.scheduleOnline}
              </Button>
            </ScheduleServiceDialog>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-5">{t.whyMattersH2}</h2>
        <div className="space-y-4 text-muted-foreground leading-relaxed">
          <p>{t.whyMattersP1}</p>
          <p>{t.whyMattersP2}</p>
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.whatWeCanDoH2}</h2>
          <div className="overflow-x-auto">
            <table className="w-full max-w-4xl text-sm border border-border bg-card rounded-md">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="p-4 font-semibold">{t.tableService}</th>
                  <th className="p-4 font-semibold">{t.tableTurnaround}</th>
                </tr>
              </thead>
              <tbody>
                {t.sameDayServices.map((r) => (
                  <tr key={r.service} className="border-b border-border last:border-0">
                    <td className="p-4 font-medium">{r.service}</td>
                    <td className="p-4 text-muted-foreground">{r.turnaround}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-6 text-muted-foreground max-w-4xl leading-relaxed">
            {t.weldingNote}
          </p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.guideH2}</h2>
        <div className="overflow-x-auto">
          <table className="w-full max-w-4xl text-sm border border-border bg-card rounded-md">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="p-4 font-semibold">{t.tableSituation}</th>
                <th className="p-4 font-semibold">{t.tableAction}</th>
              </tr>
            </thead>
            <tbody>
              {t.decisionRows.map((r) => (
                <tr key={r.situation} className="border-b border-border last:border-0">
                  <td className="p-4">{r.situation}</td>
                  <td className="p-4 font-medium">{r.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">
            <MapPin className="inline-block h-6 w-6 mr-2 -mt-1 text-primary" />
            {t.serviceAreaH2}
          </h2>
          <p className="text-muted-foreground max-w-3xl mb-8">
            {t.serviceAreaDesc}
          </p>
          <h3 className="font-semibold mb-3">{t.indianaLabel}</h3>
          <div className="flex flex-wrap gap-2 mb-8">
            {[ "Elkhart", "Granger", "Mishawaka", "Goshen", "South Bend", "Bristol", "Middlebury", "Nappanee", "Wakarusa", "New Paris", "Plymouth", "Warsaw", "Osceola", "Dunlap", "Millersburg", "Kendallville" ].map((name) => (
              <span key={name} className="bg-card border border-border rounded-md px-3 py-1.5 text-sm">{name}</span>
            ))}
          </div>
          <h3 className="font-semibold mb-3">{t.michiganLabel}</h3>
          <div className="flex flex-wrap gap-2 mb-6">
            {[ "Niles", "Sturgis", "Cassopolis", "Benton Harbor", "Kalamazoo" ].map((name) => (
              <span key={name} className="bg-card border border-border rounded-md px-3 py-1.5 text-sm">{name}</span>
            ))}
          </div>
          <p className="text-sm text-muted-foreground max-w-3xl">
            {t.nearbyNote}
          </p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.howItWorksH2}</h2>
        <ol className="space-y-5">
          {t.steps.map((s, i) => (
            <li key={i} className="flex gap-4 bg-card border border-border rounded-md p-5">
              <div className="shrink-0 h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">{i + 1}</div>
              <div>
                <p className="font-semibold">{typeof s.title === "function" ? s.title(SITE_PHONE_DISPLAY) : s.title}</p>
                <p className="text-sm text-muted-foreground leading-relaxed mt-1">{s.desc}</p>
              </div>
            </li>
          ))}
        </ol>

        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mt-14 mb-4">{t.fleetsH2}</h2>
        <p className="text-muted-foreground leading-relaxed">{t.fleetsP}</p>

        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mt-14 mb-4">{t.costH2}</h2>
        <p className="text-muted-foreground leading-relaxed">
          {t.costP}
        </p>
      </section>

      <section className="container mx-auto px-4 pb-16 md:pb-20 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.faqH2}</h2>
        <div className="divide-y divide-border border-y border-border">
          {t.faqs.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="cursor-pointer list-none flex justify-between items-start gap-4 font-medium">
                {f.q}
                <span className="text-primary text-xl leading-none mt-0.5 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="bg-primary-deep text-primary-foreground">
        <div className="container mx-auto px-4 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <ShieldCheck className="h-8 w-8 mt-1 shrink-0" />
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">{t.bannerH2}</h2>
              <p className="mt-1 text-primary-foreground">{t.bannerP}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Button asChild size="lg" className="bg-white text-primary-strong hover:bg-white/90">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
            <ScheduleServiceDialog>
              <Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10">{t.scheduleOnline}</Button>
            </ScheduleServiceDialog>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12">
        <h2 className="text-lg font-semibold mb-4">{t.relatedH2}</h2>
        <p className="text-sm text-muted-foreground">
          <Link to="/services/axle-repair/" className="text-primary-strong hover:underline underline-offset-4">{t.rel1}</Link>
          {" · "}
          <Link to="/trailer-axle-replacement-cost/" className="text-primary-strong hover:underline underline-offset-4">{t.rel2}</Link>
          {" · "}
          <Link to="/locations/" className="text-primary-strong hover:underline underline-offset-4">{t.rel3}</Link>
        </p>
      </section>
    </PageLayout>
  );
}
